import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clapperboard, MessageCircle } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { PROCESS_STEPS, IMAGES } from "../content/siteContent";
import { locProcess } from "../content/i18n_extra";
import { SpeakButton } from "./SpeakButton";

export function VideoBand() {
  const { t, lang } = useLang();
  const [phase, setPhase] = useState(0);

  const captions = [
    { big: t("video_cap1a"), small: t("video_cap1b") },
    { big: t("video_cap2a"), small: t("video_cap2b") },
    { big: t("video_cap3a"), small: t("video_cap3b") },
  ];

  useEffect(() => {
    const id = setInterval(() => setPhase((p) => (p + 1) % 3), 4000);
    return () => clearInterval(id);
  }, []);

  const narrationText = () => {
    const steps = PROCESS_STEPS.map((s, i) => locProcess(s, i, lang).t).join(". ");
    return `${t("video_title")}. ${t("video_sub")} ${steps}.`;
  };

  const openChat = () => window.dispatchEvent(new CustomEvent("open-tudi-chat"));

  return (
    <section className="bg-ice py-24 sm:py-32 border-t border-black/5" data-testid="video-band">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-5 gap-12 items-center">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7 }} className="lg:col-span-2">
          <div className="flex items-center gap-2.5 text-xs font-heading font-bold tracking-[0.3em] uppercase text-brand mb-4">
            <Clapperboard size={15} /> {t("video_kicker")}
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-ink text-balance">{t("video_title")}</h2>
          <p className="mt-5 text-lg text-sub leading-relaxed">{t("video_sub")}</p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button
              onClick={openChat}
              data-testid="video-chat-cta"
              className="inline-flex items-center gap-2 rounded-full bg-brand hover:bg-brand/90 text-white font-semibold text-sm px-7 py-3.5 transition-colors"
            >
              <MessageCircle size={16} /> Tudi
            </button>
            <SpeakButton getText={narrationText} label={t("video_listen")} testid="video-listen-cta" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="lg:col-span-3"
        >
          <div className="relative rounded-[2rem] overflow-hidden shadow-2xl shadow-brand/15 border border-black/5 bg-gradient-to-b from-[#EAF4FF] to-white min-h-[380px] flex items-end">
            <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full border-[3px] border-brand/10" />
            <div className="absolute -bottom-28 -left-20 w-72 h-72 rounded-full border-[3px] border-brand/10" />
            <div className="flex-1 px-10 pt-10 pb-8 self-center">
              <AnimatePresence mode="wait">
                <motion.div
                  key={phase + lang}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.5 }}
                >
                  <p className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-ink">{captions[phase].big}</p>
                  <p className="mt-2 text-lg text-brand font-semibold">{captions[phase].small}</p>
                </motion.div>
              </AnimatePresence>
              <div className="mt-8 flex gap-2">
                {[0, 1, 2].map((i) => (
                  <span key={i} className={`w-3 h-3 rounded-full transition-colors duration-300 ${i === phase ? "bg-brand" : "bg-brand/20"}`} />
                ))}
              </div>
            </div>
            <motion.img
              src={IMAGES.mascot}
              alt="Tudi"
              data-testid="video-band-mascot"
              animate={{ y: [0, -14, 0], rotate: [0, 1.5, -1.5, 0] }}
              transition={{ repeat: Infinity, duration: 4.5, ease: "easeInOut" }}
              className="w-[240px] sm:w-[300px] mr-4 drop-shadow-2xl shrink-0"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
